import {
  PurchaseContext,
  type PurchaseRes,
  type SiteResponse,
} from '@on3/api/src';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Brands } from '@on3/ui-lib/components/Brands';
import { Link } from '@on3/ui-lib/components/Link';
import { useAuth } from '@on3/ui-lib/index';
import { SiteKeys } from '@on3/ui-lib/utils/constants';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { formatCurrency } from '@on3/ui-lib/utils/recurly';
import { AppBadges } from '@public/components/AppBadges';
import { format } from 'date-fns';
import { type CSSProperties } from 'react';

import styles from './UserJoinSuccess.module.scss';

interface UserJoinSuccessModalProps {
  context: PurchaseContext;
  siteContext: SiteResponse;
  purchase: PurchaseRes;
}

export const UserJoinSuccessModal = ({
  context,
  siteContext: site,
  purchase,
}: UserJoinSuccessModalProps) => {
  const { formatHref } = useDynamicRoute();
  const { user } = useAuth();
  const asset = site?.defaultAssetRes;
  const showAsset =
    !!asset && ![SiteKeys.On3, SiteKeys.Rivals].includes(site.key);

  const showLineItems = !!purchase.lineItems?.length;
  const showTax = !!purchase.tax;
  const showNextBilling =
    !!purchase.nextBillingDate && !!purchase.nextBillingAmount;
  const showPaymentInfo =
    !!purchase.paymentMethodCardType &&
    !!purchase.paymentMethodLastFour &&
    !!purchase.paymentMethodExpiryMonth &&
    !!purchase.paymentMethodExpiryYear;

  const continueText =
    context === PurchaseContext.Recruits
      ? 'Continue to Recruits Dashboard'
      : 'Continue';

  const continuePath =
    context === PurchaseContext.Article
      ? undefined
      : context === PurchaseContext.Recruits
        ? '/my/profiles/'
        : site?.url || '/';

  const handleForumsContinue = () => {
    window.top?.postMessage({ type: 'RELOAD' }, '*');
  };

  return (
    <div
      className={styles.block}
      style={
        {
          '--modal-primary-color':
            site.primaryColor || styles.modalPrimaryColor,
          '--modal-button-color': site.linkColor || styles.modalButtonColor,
        } as CSSProperties
      }
    >
      <div className={styles.colorBlock}>
        <div className={styles.assets}>
          {showAsset && (
            <>
              <Avatar
                altText={asset?.altText || `${site?.name} logo`}
                domain={asset.domain}
                source={asset.source || ''}
                width={250}
              />
              <span className={styles.plus}>+</span>
            </>
          )}
          <Brands />
        </div>
      </div>
      <div className={styles.content}>
        <p className={styles.paymentStatus}>Payment successful</p>
        <h2 className={styles.title}>Welcome to {site?.name}!</h2>
        <p className={styles.text}>
          You are now subscribed to the {site.name} {purchase.planName} plan.
        </p>
        <p className={styles.text}>
          Thank you for joining the {site?.name} community. We&apos;re excited
          to have you with us! An email confirmation has been sent to {user?.e}.
        </p>
        <div className={styles.confirmation}>
          <span>Confirmation number</span>
          <span>{purchase.invoiceNumber}</span>
        </div>
        <div className={styles.orderDetails}>
          <dl className={styles.plan}>
            <dt className={styles.name}>
              {site.name} {purchase.planName}
            </dt>
            <dd className="sr-only">
              {purchase.planPrice ? formatCurrency(purchase.planPrice) : '-'}
            </dd>
          </dl>
          {(showLineItems || showTax) && (
            <div className={styles.lineItems}>
              {/* {purchase.lineItems?.map((item, i) => (
                <dl className={styles.lineItem} key={i}>
                  <dt className={styles.name}>{item.name}</dt>
                  <dd>{formatCurrency(item.price || 0)}</dd>
                </dl>
              ))} */}
              {showTax && (
                <dl className={styles.lineItem}>
                  <dt className={styles.name}>Sales Tax</dt>
                  <dd>{formatCurrency(purchase.tax || 0)}</dd>
                </dl>
              )}
            </div>
          )}
          <dl className={styles.totalCharged}>
            <dt>Total charged</dt>
            <dd>{formatCurrency(purchase.total || 0)}</dd>
          </dl>
        </div>
        <div className={styles.billingDetails}>
          {showNextBilling && (
            <dl className={styles.nextBilling}>
              <dt>Next billing period</dt>
              <dd>
                <span>{formatCurrency(purchase.nextBillingAmount || 0)}</span>
                <span className={styles.date}>
                  {format(
                    new Date(purchase.nextBillingDate || ''),
                    'MM/dd/yyyy',
                  )}
                </span>
              </dd>
            </dl>
          )}
          {showPaymentInfo && (
            <dl className={styles.paymentInfo}>
              <dt>Payment information</dt>
              <dd>
                <div>
                  <span>
                    {purchase.paymentMethodCardType} ending in{' '}
                    {purchase.paymentMethodLastFour}
                  </span>
                  <span className={styles.date}>
                    {String(purchase.paymentMethodExpiryMonth).padStart(2, '0')}
                    /{purchase.paymentMethodExpiryYear}
                  </span>
                </div>
              </dd>
            </dl>
          )}
        </div>
        {context === PurchaseContext.Forums ? (
          <button
            className={styles.continueBtn}
            onClick={handleForumsContinue}
            type="button"
          >
            {continueText}
          </button>
        ) : (
          <Link
            className={styles.continueBtn}
            forceReload
            href={formatHref({
              path: continuePath,
              options: { resetQuery: true },
            })}
          >
            {continueText}
          </Link>
        )}
        <div className={styles.download}>
          <h3>Download the {site.name} App</h3>
          <AppBadges />
        </div>
      </div>
    </div>
  );
};
